import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WaveCartProvider } from './contexts/WaveCartContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { BrandProfileProvider } from './contexts/BrandProfileContext';
import ProtectedRoute from './components/ProtectedRoute';
import HomePage from './pages/HomePage';
import BuildWavePage from './pages/BuildWavePage';
import CMDPreviewPage from './pages/CMDPreviewPage';
import ProductDetailPage from './pages/ProductDetailPage';
import PortfolioPage from './pages/PortfolioPage';
import DiscoverCreatorsPage from './pages/DiscoverCreatorsPage';
import BrandSafetyPage from './pages/BrandSafetyPage';
import CampaignsPage from './pages/CampaignsPage';
import WaveControlRoom from './pages/WaveControlRoom';
import WaveReportsPage from './pages/WaveReportsPage';
import WaveBlueprintPage from './pages/WaveBlueprintPage';
import WaveFrozenRecordPage from './pages/WaveFrozenRecordPage';
import WaveBriefingPage from './pages/WaveBriefingPage';
import InvestorOverviewGate from './pages/InvestorOverviewGate';
import InvestorAdminDashboard from './pages/InvestorAdminDashboard';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import DataDeletionPage from './pages/legal/DataDeletionPage';
import WaveWrapUpPage from './pages/WaveWrapUpPage';
// /investor redirects to /auth/login; its content was fused into /overview (2026-09).
import PublicWaveSharePage from './pages/PublicWaveSharePage';
import TemplatesPage from './pages/TemplatesPage';
import WaveFormationPage from './pages/WaveFormationPage';
import InsightsAnalyticsPage from './pages/InsightsAnalyticsPage';
import WhatsTrendingPage from './pages/WhatsTrendingPage';
import FirstPartyDataRoomPage from './pages/FirstPartyDataRoomPage';
import SupportPage from './pages/SupportPage';
import DraftsQueuePage from './pages/DraftsQueuePage';
import ContractsPage from './pages/ContractsPage';
// Auth
import LoginPage from './pages/auth/LoginPage';
import MarketingSplashPage from './pages/MarketingSplashPage';
// Phase-2 marketing rebuild — unlisted preview at /next (not linked, not
// swapped into /). Code-split via React.lazy so the ~kB payload lands only
// when the founder or director opens the URL. Suspense fallback paints the
// marketing canvas (#070708) synchronously to guarantee zero flash.
const MarketingHome = lazy(() => import('./pages/marketing/MarketingHome'));
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import DemoLoginPage from './pages/auth/DemoLoginPage';
import DemoBrandHub from './pages/DemoBrandHub';
import AuthCallbackPage from './pages/auth/AuthCallbackPage';
// Coming-soon gate
import SoonComePage from './pages/SoonComePage';
// Onboarding + Billing
import OnboardingWizard from './pages/OnboardingWizard';
import BillingPage from './pages/BillingPage';
import BillingSuccessPage from './pages/BillingSuccessPage';
import BillingCancelPage from './pages/BillingCancelPage';
import './App.css';

// Tiny redirect helper — preserves route params. Used to alias `/waves/:id/live`
// onto the canonical `/waves/:id/control`.
function WaveLiveRedirect() {
  const { id } = useParams();
  return <Navigate to={`/waves/${id}/control`} replace />;
}

// Single source of truth: when REACT_APP_LOGIN_DISABLED=true on a
// deployment, the public auth funnel goes dark and traffic that would
// otherwise see /login (or any signup-flow page) is bounced to
// /sooncome. Preview keeps developing normally because the flag is
// only set on the production env. The demo bypass (/demo?key=…) does
// NOT route through the public auth funnel, so investor walkthroughs
// stay alive by construction.
//
// Exported as a const + helper so both <Route> elements and the
// <ProtectedRoute> fallback can read the same value (no divergence).
export const LOGIN_DISABLED = process.env.REACT_APP_LOGIN_DISABLED === 'true';

function MaybeSoonCome({ children }) {
  return LOGIN_DISABLED ? <Navigate to="/sooncome" replace /> : children;
}

// MarketingShell — wraps the code-split MarketingHome in a Suspense boundary
// whose fallback is a full-viewport #070708 div. This guarantees zero flash
// while /next's chunk downloads: the shell overlays the marketing canvas on
// the entire viewport starting at first frame. MarketingHome also sets
// body.style.background on mount so scrollbar / rubber-band areas match.
function MarketingShell() {
  return (
    <Suspense
      fallback={
        <div
          style={{
            minHeight: '100vh',
            width: '100%',
            background: '#070708',
            position: 'fixed',
            inset: 0,
          }}
          data-testid="mkt-suspense-fallback"
        />
      }
    >
      <MarketingHome />
    </Suspense>
  );
}

// Smart root redirect:
//   • Authed brand users land on /home (their dashboard)
//   • Everyone else (VCs, prospects, fresh visitors) lands on the public
//     MarketingSplashPage. Public signup is closed (private beta) — existing
//     brand reps still log in via /login (direct URL). /overview is gated
//     by personalized investor keys.
function RootRedirect() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen w-full bg-[#0A0A0A]" />;
  }
  if (isAuthenticated) return <Navigate to="/home" replace />;
  return <MarketingSplashPage />;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrandProfileProvider>
      <WaveCartProvider>
      <BrowserRouter>
        <Routes>
          {/* ═══ Coming-soon gate (always available) ═══ */}
          <Route path="/sooncome" element={<SoonComePage />} />

          {/* ═══ Public auth routes ═══
              When REACT_APP_LOGIN_DISABLED=true (production during private
              beta), every auth-funnel page redirects to /sooncome. The
              demo bypass (/demo?key=…) is untouched — investor
              walkthroughs continue to work via POST /api/demo/login-as-brand. */}
          <Route path="/login" element={<MaybeSoonCome><LoginPage /></MaybeSoonCome>} />
          {/* Public signup is closed (private beta) — accounts by invitation
              only. Both /signup and /register redirect to the public splash. */}
          <Route path="/signup" element={<Navigate to={LOGIN_DISABLED ? '/sooncome' : '/'} replace />} />
          <Route path="/register" element={<Navigate to={LOGIN_DISABLED ? '/sooncome' : '/'} replace />} />
          <Route path="/forgot-password" element={<MaybeSoonCome><ForgotPasswordPage /></MaybeSoonCome>} />
          <Route path="/reset-password" element={<MaybeSoonCome><ResetPasswordPage /></MaybeSoonCome>} />
          <Route path="/verify-email" element={<MaybeSoonCome><VerifyEmailPage /></MaybeSoonCome>} />
          {/* /demo is the token-gated 12-brand investor presenter hub.
              Server returns 404 without DEMO_PRESENTER_KEY, so it's safe to
              expose in production. The legacy single-Lumina /demo flow is
              preserved at /demo/legacy and only when REACT_APP_DEMO_MODE=true. */}
          <Route path="/demo" element={<DemoBrandHub />} />
          {process.env.REACT_APP_DEMO_MODE === 'true' && (
            <Route path="/demo/legacy" element={<DemoLoginPage />} />
          )}
          {/* Hosted Google OAuth lands at /auth/callback#session_id=… */}
          <Route path="/auth/callback" element={<AuthCallbackPage />} />

          {/* ═══ Onboarding (requires auth but NOT onboarding completion) ═══ */}
          <Route path="/onboarding" element={
            <ProtectedRoute requireOnboarding={false}><OnboardingWizard /></ProtectedRoute>
          } />

          {/* ═══ Billing (requires auth but NOT onboarding) so user can complete payment mid-wizard ═══ */}
          <Route path="/billing" element={
            <ProtectedRoute requireOnboarding={false}><BillingPage /></ProtectedRoute>
          } />
          <Route path="/billing/success" element={
            <ProtectedRoute requireOnboarding={false}><BillingSuccessPage /></ProtectedRoute>
          } />
          <Route path="/billing/cancel" element={
            <ProtectedRoute requireOnboarding={false}><BillingCancelPage /></ProtectedRoute>
          } />

          {/* ═══ Main DSP routes ═══ */}
          <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
          <Route path="/build-wave" element={<ProtectedRoute><BuildWavePage /></ProtectedRoute>} />
          <Route path="/cmd-preview" element={<ProtectedRoute><CMDPreviewPage /></ProtectedRoute>} />
          <Route path="/portfolio" element={<ProtectedRoute><PortfolioPage /></ProtectedRoute>} />
          <Route path="/portfolio/:skuId" element={<ProtectedRoute><ProductDetailPage /></ProtectedRoute>} />
          <Route path="/discover" element={<ProtectedRoute><DiscoverCreatorsPage /></ProtectedRoute>} />
          <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
          <Route path="/campaigns/:id" element={<ProtectedRoute><WaveControlRoom /></ProtectedRoute>} />
          {/* iter 53: `/waves/:id/live` is a legacy alias — canonicalize on /waves/:id/control */}
          <Route path="/waves/:id/live" element={<WaveLiveRedirect />} />
          <Route path="/waves/:id/control" element={<ProtectedRoute><WaveControlRoom /></ProtectedRoute>} />
          <Route path="/waves/:id/reports" element={<ProtectedRoute><WaveReportsPage /></ProtectedRoute>} />
          <Route path="/waves/:id/blueprint" element={<ProtectedRoute><WaveBlueprintPage /></ProtectedRoute>} />
          <Route path="/waves/:id/record" element={<ProtectedRoute><WaveFrozenRecordPage /></ProtectedRoute>} />
          <Route path="/waves/:id/briefing" element={<ProtectedRoute><WaveBriefingPage /></ProtectedRoute>} />
          <Route path="/waves/:id/wrap-up" element={<ProtectedRoute><WaveWrapUpPage /></ProtectedRoute>} />
          {/* The /investor Data Room was fused into /overview (2026-09); the
              public URL redirects to login. */}
          <Route path="/investor" element={<Navigate to="/auth/login" replace />} />
          <Route path="/overview" element={<InvestorOverviewGate />} />
          <Route path="/overview/admin" element={<InvestorAdminDashboard />} />
          {/* Public legal pages — required by Meta / TikTok / YouTube reviews
              and by GDPR/CCPA. No auth, indexable, linked from auth footer. */}
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/data-deletion" element={<DataDeletionPage />} />
          <Route path="/wave/:token" element={<PublicWaveSharePage />} />
          <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
          <Route path="/waves/:id/formation" element={<ProtectedRoute><WaveFormationPage /></ProtectedRoute>} />
          <Route path="/review-queue" element={<Navigate to="/drafts" replace />} />
          <Route path="/insights" element={<ProtectedRoute><InsightsAnalyticsPage /></ProtectedRoute>} />
          <Route path="/trending" element={<ProtectedRoute><WhatsTrendingPage /></ProtectedRoute>} />
          <Route path="/footprint" element={<Navigate to="/data-room" replace />} />
          <Route path="/data-room" element={<ProtectedRoute><FirstPartyDataRoomPage /></ProtectedRoute>} />
          <Route path="/brand-safety" element={<ProtectedRoute><BrandSafetyPage /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
          <Route path="/drafts" element={<ProtectedRoute><DraftsQueuePage /></ProtectedRoute>} />
          <Route path="/contracts" element={<ProtectedRoute><ContractsPage /></ProtectedRoute>} />

          {/* Legacy redirects */}
          <Route path="/dashboard" element={<Navigate to="/home" replace />} />
          <Route path="/optimization" element={<Navigate to="/campaigns" replace />} />
          <Route path="/creators" element={<Navigate to="/trending" replace />} />
          <Route path="/analytics" element={<Navigate to="/insights" replace />} />
          <Route path="/geo-footprint" element={<Navigate to="/data-room" replace />} />
          <Route path="/social-chatter" element={<Navigate to="/trending" replace />} />

          {/* ═ Phase-2 marketing rebuild — unlisted preview at /next ═
              Not linked from anywhere. RootRedirect at / is untouched
              until final founder approval swaps this in. */}
          <Route path="/next" element={<MarketingShell />} />

          {/* Root & catch-all — unauthenticated visitors land on /login.
              `/overview` is still accessible by direct URL for investor demos. */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
      </WaveCartProvider>
      </BrandProfileProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
